import { Link } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { ADDRESS_PATHS, ADDRESS_UI } from '../constants/addresses'
import { cx } from '../helpers/format'
import { nationalPhoneDigits } from '../helpers/orders'
import { useAddressesPage } from '../hooks/useAddressesPage'
import '../styles/addresses-page.scss'

export function AddressesPage() {
  const {
    addresses,
    loading,
    draft,
    updateDraft,
    saving,
    error,
    onSubmit,
    confirmingId,
    deletingId,
    deleteError,
    askDelete,
    cancelDelete,
    confirmDelete,
  } = useAddressesPage()

  return (
    <div className="addresses-page">
      <header className="addresses-page__head">
        <h1>{ADDRESS_UI.pageTitle}</h1>
        <p>{ADDRESS_UI.pageLead}</p>
      </header>

      <div className="addresses-page__layout">
        <section className="addresses-page__saved">
          <h2 className="addresses-page__section-title">
            {ADDRESS_UI.heading}
            {addresses.length > 0 ? (
              <span className="addresses-page__count">{addresses.length}</span>
            ) : null}
          </h2>

          {loading ? (
            <p className="addresses-page__hint">{ADDRESS_UI.loading}</p>
          ) : addresses.length === 0 ? (
            <div className="addresses-page__empty">
              <p>{ADDRESS_UI.emptyTitle}</p>
              <p>{ADDRESS_UI.emptyHint}</p>
            </div>
          ) : (
            <ul className="addresses-page__list">
              {addresses.map((address) => {
                const confirming = confirmingId === address.id
                const deleting = deletingId === address.id

                return (
                  <li key={address.id} className={cx(confirming && 'is-confirming')}>
                    <div className="addresses-page__card-head">
                      <strong>{address.recipientName}</strong>
                      {confirming ? null : (
                        <div className="addresses-page__actions">
                          <Link
                            to={ADDRESS_PATHS.edit(address.id)}
                            className="addresses-page__action"
                            aria-label={ADDRESS_UI.editAria(address.recipientName)}
                          >
                            <Icon name="pencil" size={15} />
                            {ADDRESS_UI.edit}
                          </Link>
                          <button
                            type="button"
                            className="addresses-page__action addresses-page__action--danger"
                            aria-label={ADDRESS_UI.deleteAria(address.recipientName)}
                            onClick={() => askDelete(address.id)}
                            disabled={Boolean(deletingId)}
                          >
                            <Icon name="trash" size={15} />
                            {ADDRESS_UI.delete}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="addresses-page__details">
                      <span>{address.address}</span>
                      <span>
                        {address.city}
                        {address.pincode ? ` ${address.pincode}` : ''}
                      </span>
                      <span>{nationalPhoneDigits(address.recipientPhone)}</span>
                    </div>
                    {confirming ? (
                      <div className="addresses-page__confirm" role="alert">
                        <p>{deleteError ?? ADDRESS_UI.deleteConfirm}</p>
                        <div className="addresses-page__actions">
                          <button
                            type="button"
                            className="addresses-page__action"
                            onClick={cancelDelete}
                            disabled={deleting}
                          >
                            {ADDRESS_UI.deleteCancel}
                          </button>
                          <button
                            type="button"
                            className="addresses-page__action addresses-page__action--danger"
                            onClick={() => void confirmDelete(address.id)}
                            disabled={deleting}
                          >
                            <Icon name="trash" size={15} />
                            {deleting ? ADDRESS_UI.deleting : ADDRESS_UI.deleteYes}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <form className="addresses-page__form" onSubmit={(event) => void onSubmit(event)}>
          <header>
            <h2>{ADDRESS_UI.addTitle}</h2>
            <p>{ADDRESS_UI.addLead}</p>
          </header>
          <label className="addresses-page__wide">
            <span>{ADDRESS_UI.name}</span>
            <input
              value={draft.recipientName}
              onChange={(event) => updateDraft({ recipientName: event.target.value })}
              required
              maxLength={160}
              disabled={saving}
            />
          </label>
          <label className="addresses-page__wide">
            <span>{ADDRESS_UI.phone}</span>
            <input
              type="tel"
              inputMode="numeric"
              value={draft.recipientPhone}
              onChange={(event) =>
                updateDraft({
                  recipientPhone: event.target.value.replace(/\D/g, '').slice(0, 10),
                })
              }
              required
              minLength={10}
              maxLength={10}
              disabled={saving}
            />
          </label>
          <label className="addresses-page__wide">
            <span>{ADDRESS_UI.address}</span>
            <textarea
              value={draft.address}
              onChange={(event) => updateDraft({ address: event.target.value })}
              required
              maxLength={500}
              rows={3}
              disabled={saving}
            />
          </label>
          <label>
            <span>{ADDRESS_UI.city}</span>
            <input
              value={draft.city}
              onChange={(event) => updateDraft({ city: event.target.value })}
              required
              maxLength={120}
              disabled={saving}
            />
          </label>
          <label>
            <span>{ADDRESS_UI.pincode}</span>
            <input
              inputMode="numeric"
              value={draft.pincode}
              onChange={(event) =>
                updateDraft({ pincode: event.target.value.replace(/\D/g, '').slice(0, 12) })
              }
              required
              maxLength={12}
              disabled={saving}
            />
          </label>
          {error ? <p className="addresses-page__error addresses-page__wide">{error}</p> : null}
          <button type="submit" disabled={saving}>
            {saving ? ADDRESS_UI.saving : ADDRESS_UI.save}
          </button>
        </form>
      </div>
    </div>
  )
}
